import { cloneDeep } from '@apollo/client/utilities';
import {
  iconCabinet,
  iconFloor,
  iconGarage,
  iconInspiration,
  iconPaint,
  iconRoofline,
  iconWall,
  iconWindow,
} from '@hover/icons';
import type { IconProp } from '@hover/icons';
import { includes } from '@hover/utilities';
import type { TFunction } from 'i18next';
import type { ExtractRouteParams } from 'react-router';
import type { InferType } from 'yup';
import { array, number, object, string } from 'yup';

import type { SubRegion } from 'src/modules/homeownerApp/components/Customize/SubRegions';
import type {
  AiDisplayImageFragment,
  AiGeneratedImageRegionsFragment,
} from 'src/shared/graphql/generated';
import type { Route } from 'src/shared/route';

import {
  type CustomizationCategory,
  type MaterialRegion,
  type PaintRegion,
  exteriorMaterialRegions,
  exteriorPaintRegions,
  interiorMaterialRegions,
  interiorPaintRegions,
} from '../../constants/customizationCategories';
import { getImageAspect } from '../../helpers';

import type { Icon } from './icons';
import {
  CabinetIcon,
  FloorIcon,
  GarageIcon,
  PaintIcon,
  RoofIcon,
  WallsIcon,
  WindowsIcon,
} from './icons';

const ENABLED_REGION_THRESHOLD = 0.01;

export const ICONS: {
  [key: string]: IconProp;
} = {
  CabinetIcon: iconCabinet,
  FloorIcon: iconFloor,
  PaintIcon: iconPaint,
  RoofIcon: iconRoofline,
  WallsIcon: iconWall,
  WindowsIcon: iconWindow,
  GarageIcon: iconGarage,
  InspireIcon: iconInspiration,
};

export const PICTOGRAMS: { [key: string]: Icon } = {
  CabinetIcon,
  FloorIcon,
  PaintIcon,
  RoofIcon,
  WallsIcon,
  WindowsIcon,
  GarageIcon,
};

const imageRegionsSchema = array(
  object({
    region: string().required(),
    percentage: number().required().min(0).max(100),
  }),
).required();

type ImageRegions = InferType<typeof imageRegionsSchema>;
type ImageRegion = ImageRegions[number];
type ImageRegionMap = Record<ImageRegion['region'], ImageRegion['percentage']>;

export const parseImageRegions = (regions: Record<string, unknown>[]) =>
  imageRegionsSchema.isValidSync(regions) ? regions : [];

const regionMap = (regions: ImageRegions) => {
  const detectedRegions: ImageRegionMap = {};
  regions.forEach(region => {
    detectedRegions[region.region] = region.percentage;
  });
  return detectedRegions;
};

/**
 * This function is used to filter out the enabled regions from the provided regions.
 * It first checks if the image and its detected regions exist. If not, it returns the original regions.
 * If they do exist, it maps the detected regions and then iterates over the provided regions.
 * A region is considered enabled if it is not found in the mapped detected regions or
 * if its value in the mapped detected regions is greater than the ENABLED_REGION_THRESHOLD.
 * The function returns an array of enabled regions.
 *
 * @param {SubRegion[]} regions - The full list of regions
 * @param {AiGeneratedImageRegionsFragment | undefined} imageRegions - The regions detected in the image
 * @returns {SubRegion[]} - The array of enabled regions.
 */
export const getEnabledRegions = (
  regions: readonly SubRegion[],
  regionsFragment?: AiGeneratedImageRegionsFragment | null,
) => {
  // TODO: Remove as unknown as Record<string, unknown>[] after JSON graphql schema type is updated
  const imageRegions = parseImageRegions(
    regionsFragment?.regions as unknown as Record<string, unknown>[],
  );

  if (!imageRegions?.length) {
    return regions;
  }
  const imageRegionsObj = regionMap(imageRegions);

  const enabledRegions: SubRegion[] = [];

  // iterate over the regions and check if the region is enabled
  regions.forEach(region => {
    if (
      imageRegionsObj[region] &&
      imageRegionsObj[region] > ENABLED_REGION_THRESHOLD
    ) {
      enabledRegions.push(region);
    }
  });

  return enabledRegions;
};

export interface MenuItem {
  label: string;
  icon: IconProp;
  pictogram: Icon;
  category: CustomizationCategory;
  disabled?: boolean;
}

export const paintRegionsForImage = (
  image: AiDisplayImageFragment,
): readonly PaintRegion[] =>
  getImageAspect(image) === 'exterior'
    ? exteriorPaintRegions
    : interiorPaintRegions;

export const materialRegionsForImage = (
  image: AiDisplayImageFragment,
): readonly MaterialRegion[] =>
  getImageAspect(image) === 'exterior'
    ? exteriorMaterialRegions
    : interiorMaterialRegions;

export const routeParamsToCategory = (
  image: AiDisplayImageFragment,
  slug: string | undefined,
  regionSlug: string | undefined,
): CustomizationCategory | null => {
  if (slug === 'paint' && includes(paintRegionsForImage(image), regionSlug)) {
    return { type: 'paint', region: regionSlug };
  }

  if (includes(materialRegionsForImage(image), slug))
    return { type: 'material', region: slug };

  return null;
};

export type MenuRouteParams = Omit<
  ExtractRouteParams<typeof Route.HOMEOWNER_APP_CUSTOMIZE.path>,
  'aiDisplayImageId'
>;

export const categoryToRouteParams = (
  category: CustomizationCategory,
): MenuRouteParams => {
  if (category.type === 'paint')
    return {
      category: 'paint',
      region: category.region,
    };

  return { category: category.region };
};

export const getCategoryHeading = (
  t: TFunction,
  category: CustomizationCategory | null,
) => {
  if (category?.type === 'paint') {
    return t('customize.categoryHeading.paint');
  }
  if (category?.type === 'material') {
    return t(`customize.categoryHeading.${category.region}`);
  }

  return t('customize.categoryHeading.empty');
};

export const getCustomizeMenu = (t: TFunction) =>
  ({
    interior: [
      {
        label: t('customize.menu.paint'),
        icon: ICONS.PaintIcon,
        category: { type: 'paint', region: 'walls' },
        pictogram: PICTOGRAMS.PaintIcon,
      },
      {
        label: t('customize.menu.floor'),
        icon: ICONS.FloorIcon,
        category: { type: 'material', region: 'floor' },
        pictogram: PICTOGRAMS.FloorIcon,
      },
      {
        label: t('customize.menu.cabinet'),
        icon: ICONS.CabinetIcon,
        category: { type: 'material', region: 'cabinet' },
        pictogram: PICTOGRAMS.CabinetIcon,
      },
    ],
    exterior: [
      {
        label: t('customize.menu.paint'),
        icon: ICONS.PaintIcon,
        category: { type: 'paint', region: 'walls' },
        pictogram: PICTOGRAMS.PaintIcon,
      },
      {
        label: t('customize.menu.walls'),
        icon: ICONS.WallsIcon,
        category: { type: 'material', region: 'walls' },
        pictogram: PICTOGRAMS.WallsIcon,
      },
      {
        label: t('customize.menu.roof'),
        icon: ICONS.RoofIcon,
        category: { type: 'material', region: 'roof' },
        pictogram: PICTOGRAMS.RoofIcon,
      },
      {
        label: t('customize.menu.windows'),
        icon: ICONS.WindowsIcon,
        category: { type: 'material', region: 'windows' },
        pictogram: PICTOGRAMS.WindowsIcon,
      },
      {
        label: t('customize.menu.garage_door'),
        icon: ICONS.GarageIcon,
        category: { type: 'material', region: 'garage_door' },
        pictogram: PICTOGRAMS.GarageIcon,
      },
    ],
  } satisfies Record<string, MenuItem[]>);

const createAvailableMaterialRegionsFilter =
  (availableMaterialRegions: string[] | undefined) => (menuItem: MenuItem) =>
    menuItem.category.type === 'paint' ||
    !availableMaterialRegions ||
    availableMaterialRegions.length === 0 ||
    availableMaterialRegions.includes(menuItem.category.region);

export const disableMissingRegions = (
  menuItems: MenuItem[],
  regionsFragment?: AiGeneratedImageRegionsFragment | null,
  availableMaterialRegions?: string[],
) => {
  // TODO: Remove as unknown as Record<string, unknown>[] after JSON graphql schema type is updated
  const imageRegions = parseImageRegions(
    regionsFragment?.regions as unknown as Record<string, unknown>[],
  );
  const availableMaterialRegionsFilter = createAvailableMaterialRegionsFilter(
    availableMaterialRegions,
  );
  const clonedMenuItems = cloneDeep(menuItems);

  if (!imageRegions?.length) {
    return menuItems.filter(availableMaterialRegionsFilter);
  }

  const mappedDetectedRegion = regionMap(imageRegions);
  const enabledRegions: MenuItem[] = [];

  clonedMenuItems.forEach(menuItem => {
    const mappedRegionPercentage =
      mappedDetectedRegion[menuItem.category.region] ?? 0;
    if (
      menuItem.category.type === 'paint' ||
      mappedRegionPercentage > ENABLED_REGION_THRESHOLD
    ) {
      enabledRegions.push(menuItem);
    }
  });

  return enabledRegions.filter(availableMaterialRegionsFilter);
};

export const getCustomizeMenuForImage = (
  t: TFunction,
  image?: AiDisplayImageFragment | null,
  regionsFragment?: AiGeneratedImageRegionsFragment | null,
  availableMaterialsRegions?: string[],
  brandType?: string,
) => {
  const menu = getCustomizeMenu(t);

  if (!image) return undefined;

  const menuItems = menu[getImageAspect(image)];

  let filteredMenuItems = disableMissingRegions(
    menuItems,
    regionsFragment,
    availableMaterialsRegions,
  );

  if (brandType) {
    filteredMenuItems = filteredMenuItems.sort((a, b) => {
      if (a.category.region === brandType) return -1;
      if (b.category.region === brandType) return 1;
      return 0;
    });
  }

  return filteredMenuItems;
};
